import type { BoxProps } from "@qws-org/ui-kit";
import { Box, Flex } from "@qws-org/ui-kit";
import type { FC } from "react";
import React, { useEffect, useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Autoplay, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import type { Action, MainSlider as MainSliderType, Pages } from "~/core";
import { Counter } from "~/features/main-slider/components/Counter";
import { DesktopNavigationButtons } from "~/features/main-slider/components/DesktopNavigationButtons";
import { DesktopNavigationColumn } from "~/features/main-slider/components/DesktopNavigationColumn";
import { MainSliderButtons } from "~/features/main-slider/components/MainSliderButtons";
import { MobileNavigationButtons } from "~/features/main-slider/components/MobileNavigationButtons";
import { PaginationProgressDot } from "~/features/main-slider/components/PaginationProgressDot";
import { TextContent } from "~/features/main-slider/components/TextContent";
import type { ImageParams } from "~/shared/utils/directus/useDirectus";
import { useDirectus } from "~/shared/utils/directus/useDirectus";
import { getPictureData } from "~/shared/utils/get-picture-data";

export type MainSliderProps = {
  data: MainSliderType;
  getUrl: (page: Pages) => string;
  onCallAction: (action: Action) => void;
  getImageUrl: (imageId: string, params?: ImageParams) => string;
  isControlInactive: (control: Action) => boolean;
} & BoxProps;

export const MainSlider: FC<MainSliderProps> = (props) => {
  const { data, ...rest } = props;
  const { getDirectusImage } = useDirectus();
  const [activeIndex, setActiveIndex] = useState(0);
  const [progressRunId, setProgressRunId] = useState(0);
  const loop = data?.loop ?? false;
  const autoplayDelay = data.autoplay_delay ?? 5000;
  const pagination = data.pagination ?? false;
  useEffect(() => {
    setProgressRunId((v) => v + 1);
  }, [activeIndex]);
  const swiperRef = useRef<SwiperType | null>(null);
  const navigationEnabled = data?.navigation_enabled;

  if (!data?.slides || data.slides.length === 0) {
    return null;
  }

  return (
    <>
      <Flex
        width="100%"
        maxWidth={1240}
        align={"center"}
        m={{ mx: "auto" }}
        position={{ type: "relative" }}
      >
        {/* счетчик и стрелки */}
        <Flex
          display="hidden"
          position={{ type: "absolute", zIndex: 10, top: 140 }}
          md={{ display: "flex", position: { left: 20 } }}
          d={{ position: { left: 124 } }}
          align="flex-end"
          gap={16}
        >
          <Counter
            currentIndex={activeIndex}
            total={data?.slides?.length ?? 0}
          />

          {navigationEnabled && <DesktopNavigationButtons />}
        </Flex>
      </Flex>
      <Swiper
        className="w-full"
        onSlideChange={(swiper) => {
          setActiveIndex(swiper.realIndex);
        }}
        onSwiper={(swiper) => (swiperRef.current = swiper)}
        speed={data.speed ?? 500}
        autoplay={
          data.autoplay_delay
            ? {
                delay: autoplayDelay,
                pauseOnMouseEnter: data.pause_on_mouse_enter ?? true,
                disableOnInteraction: false,
              }
            : false
        }
        loop={loop}
        navigation={
          navigationEnabled
            ? { nextEl: ".custom-next", prevEl: ".custom-prev" }
            : false
        }
        centeredSlides={data.centered_slides ?? false}
        slidesPerView={data.slides_per_view ?? 1}
        grabCursor={true}
        modules={[Autoplay, Navigation]}
      >
        {data.slides?.map((slide) => {
          const slideBg = getPictureData(
            slide?.main_slider_slides_id?.background,
          );
          return (
            <SwiperSlide className="h-full" key={slide?.id}>
              <Flex
                width="100%"
                justify={"center"}
                maxWidth={768}
                height={slideBg.mHeight}
                bg={
                  slideBg.id
                    ? getDirectusImage(slideBg.id, {
                        width: slideBg.mWidth,
                        height: slideBg.mHeight,
                        format: "webp",
                        fit: "inside",
                      })
                    : slideBg.mColor
                }
                {...rest}
                md={{
                  justify: "normal",
                  bg: slideBg.mdId
                    ? getDirectusImage(slideBg.mdId, {
                        width: slideBg.mdWidth,
                        height: slideBg.mdHeight,
                        format: "webp",
                        fit: "inside",
                      })
                    : slideBg.mdColor,
                  maxWidth: 1024,
                  height: slideBg.mdHeight,

                  ...rest.md,
                }}
                d={{
                  bg: slideBg.dId
                    ? getDirectusImage(slideBg.dId, {
                        width: slideBg.dWidth,
                        height: slideBg.dHeight,
                        format: "webp",
                        fit: "inside",
                      })
                    : slideBg.dColor,

                  justify: navigationEnabled ? "space-between" : "center",
                  gap: 40,
                  maxWidth: 1920,
                  height: slideBg.dHeight,
                  ...rest.d,
                }}
              >
                {navigationEnabled && (
                  <DesktopNavigationColumn direction="left" />
                )}
                {/* контейнер внутри слайда */}
                <Flex justify={"center"}>
                  <Flex
                    flexDirection="column"
                    width={"100%"}
                    md={{ minWidth: 1024, m: { mt: 220 }, gap: 64 }}
                    gap={0}
                    m={{ mt: 0 }}
                    p={{ px: 16 }}
                  >
                    <Flex align="center" md={{ display: "hidden" }}>
                      {navigationEnabled && <MobileNavigationButtons />}
                    </Flex>

                    <TextContent
                      description={slide?.main_slider_slides_id?.description}
                      title={slide?.main_slider_slides_id?.title}
                      items={slide?.main_slider_slides_id?.items}
                    />

                    {slide?.main_slider_slides_id?.controls && (
                      <MainSliderButtons
                        controls={slide.main_slider_slides_id.controls}
                        {...rest}
                      />
                    )}
                  </Flex>
                </Flex>
                {navigationEnabled && (
                  <DesktopNavigationColumn direction="right" />
                )}
              </Flex>
            </SwiperSlide>
          );
        })}
      </Swiper>
      {pagination && (
        <Box position={{ type: "relative" }}>
          <Flex
            position={{ type: "absolute", zIndex: 10, top: -28 }}
            width={"100%"}
            height={"100%"}
            justify={"center"}
            display={"hidden"}
            md={{ display: "flex" }}
          >
            <Flex gap={4}>
              {data?.slides?.map((_, i) => {
                const state =
                  i < activeIndex
                    ? "fulfilled"
                    : i === activeIndex
                      ? "current"
                      : "future";

                return (
                  <PaginationProgressDot
                    key={`${i}-${activeIndex}`}
                    duration={autoplayDelay}
                    state={state}
                    resetKey={state === "current" ? progressRunId : 0}
                  />
                );
              })}
            </Flex>
          </Flex>
        </Box>
      )}
    </>
  );
};
